import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, CreditCard, Clock, CheckCircle, AlertCircle, Trash2, Star } from "lucide-react";
import { Button } from "./Button";
import { Card } from "./card";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { deleteMilestone } from "../../redux/Slices/milestoneSlice";
import { fetchContract } from "../../redux/Slices/contractSlice";
import { Milestone, MilestoneStatusEnum, MilestonePaymentStatusEnum, MilestonePaymentStatusRef, ContractStatusEnum } from "../../modules/shared/model";
import { GlobalService } from "../../modules";
import PaymentModal from "./PaymentModal";
import PaymentRequestModal from "./PaymentRequestModal";
import { Escrow } from "../../services/escrowAPI";
import { getAllCurrencies } from "../../redux/Actions/AuthAction";
import ClientReviewModal from "./ClientReviewModal";
import { areAllMilestonesReleased, isMilestoneReleased, hasUserReviewedMilestone } from "../../utils/reviewUtils";
import { checkUserBillingInfo } from "../../utils/billingUtils";
import { calculateUserAverageRating } from "../../utils/ratingCalculationUtils";
import BillingAddressRequirementDialog from "./BillingAddressRequirementDialog";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";
import { useTranslation } from "react-i18next";

interface Props {
	isOpen: boolean;
	onClose: () => void;
	contractId: string;
}

interface MilestoneWithEscrow extends Omit<Milestone, "paymentStatus"> {
	escrow?: Escrow;
	paymentStatus?: string; // Keep as string for display purposes, separate from Milestone.paymentStatus
	reviews?: any[]; // Reviews for this milestone
}

const MilestoneDialog: React.FC<Props> = ({ isOpen, onClose, contractId }) => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const { loading: contractsLoading, current: currentContract } = useAppSelector((s: any) => s.contracts);
	const { currencies } = useAppSelector((s: any) => s.auth);
	const [milestones, setMilestones] = useState<MilestoneWithEscrow[]>([]);
	const [loading, setLoading] = useState(false);
	const [initialLoading, setInitialLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [selectedMilestone, setSelectedMilestone] = useState<MilestoneWithEscrow | null>(null);
	const [showPaymentModal, setShowPaymentModal] = useState(false);
	const [showPaymentRequestModal, setShowPaymentRequestModal] = useState(false);
	const [selectedPaymentRequest, setSelectedPaymentRequest] = useState<any>(null);
	const [selectedMilestoneId, setSelectedMilestoneId] = useState<string | null>(null);
	const [showReviewModal, setShowReviewModal] = useState(false);
	const [selectedMilestoneForReview, setSelectedMilestoneForReview] = useState<MilestoneWithEscrow | null>(null);
	const [showBillingAddressDialog, setShowBillingAddressDialog] = useState(false);
	const [selectedMilestoneForPayment, setSelectedMilestoneForPayment] = useState<MilestoneWithEscrow | null>(null);
	const { user } = useAppSelector((s: any) => s.auth);

	useEffect(() => {
		if (isOpen && contractId) {
			setInitialLoading(true);
			setError(null);
			
			// Load all required data in parallel
			const loadData = async () => {
				try {
					// Validate contractId before fetching
					if (!contractId || contractId === "undefined" || contractId === "null") {
						throw new Error("Invalid contract ID");
					}

					// Fetch contract data first
					const result = await dispatch(fetchContract(contractId));
					
					// Check if the fetch was successful
					if (result.type.includes("rejected")) {
						throw new Error("Failed to load contract data");
					}
					
					// Only fetch currencies if not already loaded
					if (!currencies || currencies.length === 0) {
						await dispatch(getAllCurrencies({ isShowLoader: false }));
					}
				} catch (error: any) {
					console.error("Failed to load milestone data:", error);
					setError(error?.message || t("milestoneDialog.failedToLoad"));
					// Don't close the dialog, just show the error
				} finally {
					setInitialLoading(false);
				}
			};
			
			loadData();
		}
	}, [isOpen, contractId, dispatch, currencies]);

	// Effect to process milestones when contract data is loaded
	useEffect(() => {
		if (currentContract && currentContract.id === contractId) {
			if (currentContract.milestones) {
				processMilestonesFromContract(currentContract.milestones);
			} else {
				setMilestones([]);
			}
		}
	}, [currentContract, contractId]);


	const processMilestonesFromContract = (milestonesData: Milestone[]) => {
		try {
			setLoading(true);
			setError(null);

			if (milestonesData.length > 0) {
				setEscrowMilestone(milestonesData);
			} else {
				setMilestones([]);
			}
		} catch (err: any) {
			setError(err?.message || t("milestoneDialog.failedToLoad"));
			GlobalService.showToast("error", t("common.error"), t("milestoneDialog.failedToLoad"));
		} finally {
			setLoading(false);
		}
	};

	const setEscrowMilestone = (milestonesData: Milestone[]) => {
		// Get escrows from contract data instead of making separate API call
		const escrows: any = currentContract?.escrows || [];

		// Map escrows to milestones
		const milestonesWithEscrows = milestonesData.map((milestone: Milestone) => {
			const escrow = escrows.find((e: Escrow) => e.milestone?.id === milestone.id);
			let paymentStatus = "pending"; // Default status

			if (escrow) {
				const statusName = escrow.status?.name?.toLowerCase() || escrow.status?.toString().toLowerCase() || "";
				switch (statusName) {
					case "paid":
					case "funded":
					case "completed":
						paymentStatus = "paid";
						break;
					case "pending":
					case "created":
						paymentStatus = "pending";
						break;
					case "requested":
						paymentStatus = "requested";
						break;
					case "failed":
					case "cancelled":
						paymentStatus = "failed";
						break;
					case "released":
						paymentStatus = "released";
						break;
					case "refunded":
						paymentStatus = "refunded";
						break;
					case "rejected":
						paymentStatus = "rejected";
						break;
					default:
						paymentStatus = "pending";
				}
			}

			return {
				...milestone,
				escrow,
				paymentStatus
			};
		});

		setMilestones(milestonesWithEscrows);
	};

	// ✅ BUG FIX: Added event parameter and preventDefault/stopPropagation
	const handlePayNow = (milestone: MilestoneWithEscrow, event?: React.MouseEvent) => {
		// CRITICAL: Prevent default button behavior to avoid page redirect
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}
		
		// Check if user has billing information
		const billingCheck = checkUserBillingInfo(user);
		
		if (!billingCheck.hasBillingInfo) {
			// Store the milestone for auto-opening payment modal after billing setup
			setSelectedMilestoneForPayment(milestone);
			// Show billing address requirement dialog
			setShowBillingAddressDialog(true);
			return;
		}

		// User has billing info, proceed with payment
		setSelectedMilestone(milestone);
		setShowPaymentModal(true);
	};

	const handlePaymentSuccess = async () => {
		setShowPaymentModal(false);
		setSelectedMilestone(null);
		// Reload contract to update payment status
		await dispatch(fetchContract(contractId));
		
		// Close the milestone dialog after successful payment
		onClose();
		// Show success message with latest escrow status
	};

	const handleReleasePayment = async (milestone: MilestoneWithEscrow) => {
		try {
			if (!milestone.escrow) {
				GlobalService.showToast("error", t("common.error"), t("milestoneDialog.noEscrow"));
				return;
			}
			
			// Create a payment request object similar to what's expected by PaymentRequestModal
			const paymentRequest = {
				id: milestone.escrow.id,
				amount: milestone.escrow.totalAmount || milestone.escrow.amount || 0,
				currency: milestone.escrow.currency?.name || "USD",
				status: milestone.escrow.status?.name || "pending",
				description: `Payment for milestone: ${milestone.description}`,
				createdAt: milestone.escrow.createdAt,
				escrow: milestone.escrow
			};

			// Set the payment request data and show the modal
			setSelectedPaymentRequest(paymentRequest);
			setSelectedMilestoneId(milestone.id);
			setShowPaymentRequestModal(true);
		} catch (error: any) {
			console.error("Failed to prepare payment request:", error);
			GlobalService.showToast("error", t("common.error"), t("milestoneDialog.milestoneDeleteError") + ": " + (error?.message || t("common.error")));
		}
	};

	// Handle payment request modal close
	const handleClosePaymentRequestModal = (messageData?: any) => {
		setShowPaymentRequestModal(false);
		setSelectedPaymentRequest(null);
		setSelectedMilestoneId(null);
		dispatch(fetchContract(contractId));
	};

	const handleOpenReviewModal = (milestone: MilestoneWithEscrow) => {
		setSelectedMilestoneForReview(milestone);
		setShowReviewModal(true);
	};

	const handleCloseReviewModal = () => {
		setShowReviewModal(false);
		setSelectedMilestoneForReview(null);
	};

	const handleReviewSubmitted = () => {
		// Refresh contract data after review submission
		dispatch(fetchContract(contractId));
	};

	const handleBillingSaved = () => {
		// Check if user now has billing information
		const billingCheck = checkUserBillingInfo(user);
		
		if (billingCheck.hasBillingInfo && selectedMilestoneForPayment) {
			// Open payment modal for the stored milestone
			setSelectedMilestone(selectedMilestoneForPayment);
			setShowPaymentModal(true);
			setSelectedMilestoneForPayment(null);
		}
	};

	const handleDeleteMilestone = async (milestone: MilestoneWithEscrow) => {
		try {
			// Confirm deletion
			const confirmed = window.confirm(t("milestoneDialog.confirmDelete"));
			if (!confirmed) return;

			// Delete the milestone
			await dispatch(deleteMilestone(milestone.id));

			// Reload contract to update the list
			await dispatch(fetchContract(contractId));

			GlobalService.showToast("success", t("common.success"), t("milestoneDialog.milestoneDeletedSuccess"));
		} catch (error: any) {
			console.error("Failed to delete milestone:", error);
			GlobalService.showToast("error", t("common.error"), t("milestoneDialog.milestoneDeleteError") + ": " + (error?.message || t("common.error")));
		}
	};

	const getStatusIcon = (status: MilestoneStatusEnum) => {
		switch (Number(status)) {
			case MilestoneStatusEnum.Completed:
				return <CheckCircle className="w-5 h-5 text-green-500" />;
			case MilestoneStatusEnum.Active:
				return <Clock className="w-5 h-5 text-blue-500" />;
			case MilestoneStatusEnum.Pending:
				return <Clock className="w-5 h-5 text-yellow-500" />;
			default:
				return <AlertCircle className="w-5 h-5 text-gray-500" />;
		}
	};

	const getStatusText = (status: MilestoneStatusEnum) => {
		switch (Number(status)) {
			case MilestoneStatusEnum.Completed:
				return t("milestoneDialog.completed");
			case MilestoneStatusEnum.Active:
				return t("milestoneDialog.active");
			case MilestoneStatusEnum.Pending:
				return t("milestoneDialog.pending");
			case MilestoneStatusEnum.Accepted:
				return t("milestoneDialog.accepted");
			case MilestoneStatusEnum.Rejected:
				return t("milestoneDialog.rejected");
			default:
				return t("milestoneDialog.unknown");
		}
	};

	const getPaymentStatusText = (paymentStatus: string) => {
		switch (paymentStatus) {
			case "paid":
			case "completed":
				return t("milestoneDialog.paid");
			case "pending":
			case "created":
				return t("milestoneDialog.pendingPayment");
			case "requested":
				return t("milestoneDialog.paymentRequested");
			case "failed":
			case "cancelled":
				return t("milestoneDialog.paymentFailed");
			case "released":
				return t("milestoneDialog.released");
			case "refunded":
				return t("milestoneDialog.refunded");
			case "rejected":
				return t("milestoneDialog.rejected");
			default:
				return t("milestoneDialog.noPayment");
		}
	};

	const getPaymentStatusColor = (paymentStatus: string) => {
		switch (paymentStatus) {
			case "paid":
			case "completed":
				return "text-green-600 bg-green-100";
			case "pending":
			case "created":
				return "text-yellow-600 bg-yellow-100";
			case "requested":
				return "text-blue-600 bg-blue-100";
			case "failed":
			case "cancelled":
				return "text-red-600 bg-red-100";
			case "released":
				return "text-green-600 bg-green-100";
			case "refunded":
				return "text-green-600 bg-green-100";
			case "rejected":
				return "text-red-600 bg-red-100";
			default:
				return "text-gray-600 bg-gray-100";
		}
	};

	const getEscrowStatusText = (milestone: MilestoneWithEscrow) => {
		if (!milestone.escrow) {
			return t("milestoneDialog.noEscrow");
		}
		
		const statusName = milestone.escrow.status?.name || milestone.escrow.status?.toString() || t("milestoneDialog.unknown");
		return `${t("milestoneDialog.escrow")} ${statusName}`;
	};

	// Star rating display component with decimal support
	const StarRatingDisplay = ({ rating, size = "sm" }: { rating: number; size?: "sm" | "md" | "lg" }) => {
		const sizeClasses = {
			sm: "h-4 w-4",
			md: "h-5 w-5",
			lg: "h-6 w-6"
		};

		return (
			<div className="flex items-center">
				{[1, 2, 3, 4, 5].map((star) => {
					const isHalfFilled = rating >= star - 0.5 && rating < star;
					const isFilled = rating >= star;
					
					return (
						<div key={star} className="relative">
							<Star
								className={`${sizeClasses[size]} ${
									isFilled
										? "fill-yellow-400 text-yellow-400"
										: "text-gray-300"
								}`}
							/>
							{isHalfFilled && (
								<div className="absolute left-0 top-0 w-1/2 h-full overflow-hidden">
									<Star
										className={`${sizeClasses[size]} fill-yellow-400 text-yellow-400`}
									/>
								</div>
							)}
						</div>
					);
				})}
			</div>
		);
	};

	const canPayMilestone = (milestone: MilestoneWithEscrow, milestoneIndex: number): boolean => {
		// Hide Pay Now button if milestone is rejected
		if (Number(milestone.status?.id) === MilestoneStatusEnum.Rejected) {
			return false;
		}

		// Only show Pay Now button if payment is NOT done
		// Hide button for: paid, completed, released, requested milestones
		const isPaymentDone = milestone.paymentStatus === "paid" ||
			milestone.paymentStatus === "completed" ||
			milestone.paymentStatus === "released" ||
			milestone.paymentStatus === "funded" ||
			milestone.paymentStatus === "requested";

		if (isPaymentDone) {
			return false;
		}

		if(currentContract && Number(currentContract.status.id) === ContractStatusEnum.Completed ||  Number(currentContract.status.id) === ContractStatusEnum.Closed ){
			return false;
		}

		// For the first milestone (index 0), allow payment if not paid
		if (milestoneIndex === 0) {
			return true;
		}

		// For subsequent milestones, check if ALL previous milestones are paid
		// Only allow payment if the immediate previous milestone is paid
		const previousMilestone = milestones[milestoneIndex - 1];
		if (!previousMilestone) {
			return false;
		}

		const isPreviousMilestonePaid = previousMilestone.paymentStatus === "paid" ||
			previousMilestone.paymentStatus === "completed" ||
			previousMilestone.paymentStatus === "released" ||
			previousMilestone.paymentStatus === "funded";

		return isPreviousMilestonePaid;
	};

	if (!isOpen) return null;

	return (
		<AnimatePresence>
			<motion.div
				key="milestone-dialog-backdrop"
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				className="fixed inset-0 bg-black bg-opacity-50 z-[10002] flex items-center justify-center p-4"
				onClick={(e) => {
					if (e.target === e.currentTarget) {
						onClose();
					}
				}}
			>
				<motion.div
					initial={{ scale: 0.95, y: 20 }}
					animate={{ scale: 1, y: 0 }}
					exit={{ scale: 0.95, y: 20 }}
					className="bg-white rounded-xl w-full max-w-sm sm:max-w-2xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto mx-2 sm:mx-4 relative"
					onClick={(e) => e.stopPropagation()}
				>
					{/* Loading overlay to prevent interaction during initial loading */}
					{initialLoading && (
						<div className="absolute inset-0 bg-white bg-opacity-90 z-10 flex items-center justify-center">
							<div className="flex flex-col items-center gap-4">
								<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
								<div className="text-center">
								<span className="text-base font-medium text-gray-700">{t("milestoneDialog.loadingContractData")}</span>
								<p className="text-sm text-gray-500 mt-1">{t("milestoneDialog.pleaseWaitFetchingMilestones")}</p>
								</div>
							</div>
						</div>
					)}
					<div className="sticky top-0 bg-white border-b p-4 sm:p-6 flex justify-between items-center">
						<div className="flex-1 min-w-0">
							<h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 truncate">{t("milestoneDialog.contractMilestones")}</h2>
							<p className="text-sm sm:text-base text-gray-600 mt-1 hidden sm:block">{t("milestoneDialog.trackAndManage")}</p>
						</div>
						<button
							onClick={onClose}
							className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0 ml-2"
						>
							<X className="h-5 w-5 sm:h-6 sm:w-6 text-gray-500" />
						</button>
					</div>

					<div className="p-4 sm:p-6">

						{/* Initial loading state - show when dialog first opens */}
						{initialLoading ? (
							<div className="flex flex-col items-center justify-center py-12 sm:py-16">
								<div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-2 border-blue-600"></div>
								<span className="mt-4 text-base sm:text-lg text-gray-700 font-medium text-center">{t("milestoneDialog.loadingData")}</span>
								<span className="mt-2 text-sm text-gray-500 text-center">{t("milestoneDialog.waitMessage")}</span>
							</div>
						) : loading ? (
							<div className="flex flex-col sm:flex-row items-center justify-center py-8 sm:py-12">
								<div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-blue-600"></div>
								<span className="ml-0 sm:ml-3 mt-2 sm:mt-0 text-sm sm:text-base text-gray-600">{t("milestoneDialog.loadingContractData")}</span>
							</div>
						) : error ? (
							<div className="text-center py-8 sm:py-12">
								<AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 text-red-500 mx-auto mb-4" />
								<p className="text-sm sm:text-base text-red-600 mb-4 px-4">{error}</p>
								<Button onClick={() => dispatch(fetchContract(contractId))} variant="outline" className="text-sm sm:text-base">
									{t("milestoneDialog.tryAgain")}
								</Button>
							</div>
						) : milestones.length === 0 ? (
							<div className="text-center py-8 sm:py-12">
								<Clock className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-4" />
								<p className="text-sm sm:text-base text-gray-600 px-4">{t("milestoneDialog.noMilestonesMessage")}</p>
							</div>
						) : (
							<div className="space-y-3 sm:space-y-4">
								{milestones.map((milestone, index) => (
									<Card key={milestone.id || `milestone-${contractId}-${index}`} className="p-4 sm:p-6">
										<div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
											<div className="flex-1">
												<div className="flex items-start gap-3 mb-3">
													<div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
														<span className="text-blue-600 font-semibold text-xs sm:text-sm">
															{index + 1}
														</span>
													</div>
													<div className="flex-1 min-w-0">
														<h3 className="text-base sm:text-lg font-semibold text-gray-900">
															{t("milestoneDialog.milestone")} {index + 1}
														</h3>
														<div className="flex items-center gap-2 mt-1">
															{getStatusIcon(milestone.status?.id)}
															<span className="text-xs sm:text-sm text-gray-600">
																{getStatusText(milestone.status?.id)}
															</span>
														</div>
														{milestone.escrow && (
															<div className="flex items-center gap-2 mt-1">
																<span className="text-xs text-gray-500">
																	{getEscrowStatusText(milestone)}
																</span>
															</div>
														)}
													</div>
													
													{/* Reviews from other users */}
													{(() => {
														// Check if milestone has reviews from other users
														if (!milestone.reviews || milestone.reviews.length === 0) {
															return null;
														}

														// Calculate average rating for the professional (user being reviewed)
														const professionalRatingData = calculateUserAverageRating(milestone.reviews, user?.id || "");
														
														// Only show if there are reviews and rating > 0
														if (professionalRatingData.average > 0) {
															return (
																<div className="flex flex-col items-end text-right ml-auto sm:ml-0">
																	<div className="flex items-center gap-1 mb-1">
																		<StarRatingDisplay rating={professionalRatingData.average} size="sm" />
																		<span className="text-xs sm:text-sm font-medium text-gray-900">
																			{professionalRatingData.average.toFixed(1)}
																		</span>
																	</div>
																	<span className="text-xs text-gray-500">
																		{professionalRatingData.count} {professionalRatingData.count !== 1 ? t("milestoneDialog.reviews") : t("milestoneDialog.review")}
																	</span>
																</div>
															);
														}

														return null;
													})()}
												</div>

                                                <div className="mb-4">
                                                    <p className="text-sm sm:text-base text-gray-700 mb-2">{milestone.description}</p>
                                                    <div className="space-y-2">
                                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                                                            <span>
                                                                <strong>{t("milestoneDialog.amount")}</strong> {getDynamicCurrencySymbol()}{milestone.amount}
                                                            </span>
															{milestone.dueDate && (
																<span>
																	<strong>{t("milestoneDialog.due")}</strong> {new Date(milestone.dueDate).toLocaleDateString()}
																</span>
															)}
                                                        </div>
                                                        <div className="bg-gray-50 p-3 rounded-lg">
                                                            <div className="flex justify-between text-xs sm:text-sm">
                                                                <span className="text-gray-600">{t("milestoneDialog.amount")}</span>
                                                                <span className="font-medium">{getDynamicCurrencySymbol()}{milestone.amount}</span>
                                                            </div>
                                                            {(Number((milestone as any).emergencyCost) > 0) && (
                                                                <div className="flex justify-between text-xs sm:text-sm">
                                                                    <span className="text-red-600">{t("milestoneDialog.emergencyCost")}</span>
                                                                    <span className="font-medium text-red-600">{getDynamicCurrencySymbol()}{Number((milestone as any).emergencyCost).toFixed(2)}</span>
                                                                </div>
                                                            )}
                                                            {(Number((milestone as any).emergencyCost) > 0) && (
                                                                <div className="flex justify-between text-xs sm:text-sm font-semibold border-t pt-2 mt-2">
                                                                    <span>{t("milestoneDialog.totalMilestoneAmount")}</span>
                                                                    <span className="text-base sm:text-lg">{getDynamicCurrencySymbol()}{(Number(milestone.amount) + Number((milestone as any).emergencyCost || 0)).toFixed(2)}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

												<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
													<div className="flex flex-col sm:flex-row sm:items-center gap-2">
														<span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(milestone.paymentStatus || "no_payment")}`}>
															{getPaymentStatusText(milestone.paymentStatus || "no_payment")}
														</span>
														{milestone.paymentStatus === "refunded" && (
															<span className="px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
																<span className="hidden sm:inline">{t("milestoneDialog.fullRefundMessage")}</span>
																<span className="sm:hidden">{t("milestoneDialog.fullRefundShort")}</span>
															</span>
														)}
														{milestone.paymentStatus === "released" && (
															<span className="px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
																<span className="hidden sm:inline">{t("milestoneDialog.paymentReleasedMessage")}</span>
																<span className="sm:hidden">{t("milestoneDialog.paymentReleasedShort")}</span>
															</span>
														)}
													</div>

													<div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
														{/* Show Delete button for pending milestones */}
														{/* {Number(milestone.status?.id) === MilestoneStatusEnum.Pending && (
															<Button
																onClick={() => handleDeleteMilestone(milestone)}
																className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm"
																variant="default"
															>
																<Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
																<span className="hidden sm:inline">Delete</span>
															</Button>
														)} */}

														{/* Show Release Payment button for accepted milestones that are not already released */}
														{Number(milestone.status?.id) === MilestoneStatusEnum.Accepted && milestone.paymentStatus !== "released" && Number(currentContract?.status?.id) !== ContractStatusEnum.Closed && Number(currentContract?.status?.id) !== ContractStatusEnum.Completed && (
															<Button
																onClick={() => handleReleasePayment(milestone)}
																className="flex items-center gap-1 sm:gap-2 bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm px-3 sm:px-4"
															>
																<CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
																<span className="hidden sm:inline">{t("milestoneDialog.releasePayment")}</span>
																<span className="sm:hidden">{t("milestoneDialog.release")}</span>
															</Button>
														)}

														{/* Show Rejected message for rejected milestones */}
														{Number(milestone.status?.id) === MilestoneStatusEnum.Rejected && (
															<div className="flex items-center gap-2 text-xs sm:text-sm text-red-600 bg-red-50 px-2 sm:px-3 py-2 rounded-lg">
																<X className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
																<span className="hidden sm:inline">{t("milestoneDialog.rejectedByProfessional")}</span>
																<span className="sm:hidden">{t("milestoneDialog.rejected")}</span>
															</div>
														)}

														{/* Show Review button for released milestones only if user hasn't already reviewed */}
														{isMilestoneReleased(milestone) && !hasUserReviewedMilestone(milestone, user?.id || "") && (
															<Button
																onClick={() => handleOpenReviewModal(milestone)}
																className="flex items-center gap-1 sm:gap-2 bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm px-3 sm:px-4"
															>
																<Star className="w-3 h-3 sm:w-4 sm:h-4" />
																<span className="hidden sm:inline">{t("milestoneDialog.rateProfessional")}</span>
																<span className="sm:hidden">{t("milestoneDialog.rate")}</span>
															</Button>
														)}

														{/* ✅ BUG FIX: Updated onClick to pass event parameter */}
														{/* Show Pay Now button only for first unpaid milestone or subsequent milestones if previous is paid */}
														{canPayMilestone(milestone, index) ? (
															<Button
																onClick={(e) => handlePayNow(milestone, e)}
																className={`flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-3 sm:px-4 ${milestone.paymentStatus === "failed" || milestone.paymentStatus === "cancelled"
																	? "border-red-200 text-red-600 hover:bg-red-50 bg-white"
																	: "bg-blue-600 hover:bg-blue-700 text-white"
																	}`}
																variant={milestone.paymentStatus === "failed" || milestone.paymentStatus === "cancelled" ? "outline" : "default"}
															>
																<CreditCard className="w-3 h-3 sm:w-4 sm:h-4" />
																{milestone.paymentStatus === "failed" || milestone.paymentStatus === "cancelled"
																	? <span className="hidden sm:inline">{t("milestoneDialog.retryPayment")}</span>
																	: <span className="hidden sm:inline">{t("milestoneDialog.payNow")}</span>
																}
																{milestone.paymentStatus === "failed" || milestone.paymentStatus === "cancelled"
																	? <span className="sm:hidden">{t("milestoneDialog.retry")}</span>
																	: <span className="sm:hidden">{t("milestoneDialog.pay")}</span>
																}
															</Button>
														) : index > 0 && milestone.paymentStatus !== "paid" && milestone.paymentStatus !== "completed" && milestone.paymentStatus !== "released" && milestone.paymentStatus !== "funded" && milestone.paymentStatus !== "requested" ? (
															<div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
																<Clock className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
																<span className="hidden sm:inline">{t("milestoneDialog.completePreviousMilestone")}</span>
																<span className="sm:hidden">{t("milestoneDialog.completePrevious")}</span>
															</div>
														) : null}
													</div>
												</div>
											</div>
										</div>
									</Card>
								))}
							</div>
						)}
					</div>
				</motion.div>
			</motion.div>

			{/* Payment Modal */}
			{selectedMilestone && (
				<PaymentModal
					key="payment-modal"
					isOpen={showPaymentModal}
					onClose={() => {
						setShowPaymentModal(false);
						setSelectedMilestone(null);
						dispatch(fetchContract(contractId));
					}}
					selectedContract={currentContract}
					onPaymentSuccess={handlePaymentSuccess}
					milestone={selectedMilestone}
					clientId="current-user-id" // TODO: Get from auth context
					professionalId="professional-id" // TODO: Get from contract data
				/>
			)}

			{/* Payment Request Modal */}
			{showPaymentRequestModal && (
				<PaymentRequestModal
					key="payment-request-modal"
					isOpen={showPaymentRequestModal}
					onClose={handleClosePaymentRequestModal}
					paymentRequest={selectedPaymentRequest}
					loading={loading}
					milestoneId={selectedMilestoneId || undefined}
				/>
			)}
			
			{/* Client Review Modal */}
			{selectedMilestoneForReview && (
				<ClientReviewModal
					key="client-review-modal"
					isOpen={showReviewModal}
					onClose={handleCloseReviewModal}
					milestoneId={selectedMilestoneForReview.id}
					milestone={selectedMilestoneForReview}
					professionalId={selectedMilestoneForReview.escrow?.professional?.id || ""}
					contractId={contractId}
					onReviewSubmitted={handleReviewSubmitted}
				/>
			)}

			{/* Billing Address Requirement Dialog */}
			{showBillingAddressDialog && (
				<BillingAddressRequirementDialog
					key="billing-address-dialog"
					isOpen={showBillingAddressDialog}
					onClose={() => {
						setShowBillingAddressDialog(false);
						setSelectedMilestoneForPayment(null);
					}}
					onBillingSaved={handleBillingSaved}
					userRole={Number(user?.role?.id)}
					action="payment"
				/>
			)}
		</AnimatePresence>
	);
};

export default MilestoneDialog;
